/**
 * Transcription Pipeline Hook — v5.6.0
 * Orchestrates the transcription process.
 *
 * Pipeline:
 *   Phase 1: Decode audio (client-side)
 *   Phase 2a: Large files (>4MB) → upload to Supabase Storage → AssemblyAI Universal-2
 *             (Afrikaans + English code-switching, diarisation, 5GB/10hr native)
 *   Phase 2b: Small files (<4MB) → base64 → prompted Gemini path (GEMINI_TRANSCRIBE_MODEL; multilingual, code-switching)
 *   Phase 2c: Fallback to Deepgram/Groq if Gemini fails (circuit breaker)
 *   Phase 3:  Intelligence synthesis (optional, Gemini)
 *
 * AssemblyAI replaces the old Gemini two-step upload (gemini-upload → transcribe-gemini)
 * for large files. It accepts Supabase signed URLs directly — no Gemini Files API needed,
 * no chunking needed. Handles 45–120 min meetings natively.
 */

import { useCallback, useRef } from 'react';
import { useTranscriptionStore } from '../store/useTranscriptionStore';
import { useAuthStore } from '../store/useAuthStore';
import { useAudioProcessing } from './useAudioProcessing';
import { TranscriptionService, GeminiUnavailableError, TranscriptionStillProcessingError, TranscriptionFailedError } from '../services/transcriptionService';
import { sha256Hex, postSaveTranscription } from '../services/saveTranscription';
import type { TranscribeResult, TranscriptionUtterance } from '../services/transcriptionService';
import { ForensicAudio } from '../utils/audioEffects';
import { splitRefineWindows } from '../utils/refineChunking';
import { looksLikeEnglishTranslation } from '../utils/languageLeakage';
import {
  computeChunkWindows,
  minPlausibleSegments,
  renderTailContext,
  buildContinuationHint,
  transcribeChunkWithRetry,
  tooManyChunksFailed,
  gapMarkerText,
  CHUNK_WINDOW_S,
  CHUNK_OVERLAP_S,
  CHUNK_SAMPLE_RATE,
} from '../utils/chunkAudio';
import { SYSTEM_STATUS, API_CONFIG } from '../config/constants';
import { AppStep, TranscriptionOptions, OutputDirectives } from '../types';
import type { LogCallback } from '../types';
import { supabase } from '../lib/supabase';
import { paygFloorUpload } from '../services/paygFloor';
import { assertEngineAllowed, isEngineAllowed, engineUnavailableMessage, isUndetectableDutchAfrikaans } from '../services/engineRouting';

// Max payload size for Vercel serverless functions (~4.5MB).
// Files larger than this are uploaded to Supabase Storage and transcribed via URL.
const DIRECT_UPLOAD_LIMIT = 4 * 1024 * 1024; // 4MB (conservative)
// Supabase Storage free tier file size limit (hard cap, cannot increase)
const STORAGE_FILE_LIMIT = 50 * 1024 * 1024; // 50MB

// Max safe chunk size for Supabase Storage (hard 50MB cap, use 45MB with margin)
const CHUNK_SIZE_LIMIT = 45 * 1024 * 1024; // 45MB
// Minimum sample rate — 4kHz is above telephone quality, Gemini handles it well
const MIN_SAMPLE_RATE = 4000;
// Maximum sample rate for compressed audio — 8kHz is fine for speech
const MAX_SAMPLE_RATE = 8000;

// Above this duration a single Gemini transcription call blows Vercel's 60s
// generation window (→ 504), so long Afrikaans recordings are transcribed as
// sequential context-passing chunks on Gemini (see the Afrikaans >8 min branch).
// Afrikaans never goes to AssemblyAI/Deepgram/Groq (engineRouting forbids it:
// AssemblyAI-'af' is known Dutch garbage), so a quality miss or a dead Gemini key
// fails loud with a truthful "unavailable" error instead.
const GEMINI_SINGLE_CALL_MAX_S = 8 * 60;

// South African indigenous languages that run on Gemini ONLY (no Lelapa key).
// Like Afrikaans, AssemblyAI/Deepgram mistranscribe these, so they get a verbatim
// Gemini language hint and fail loud rather than falling back to garbage. (beta)
const GEMINI_ONLY_LANGS = ['zu', 'xh', 'st', 'tn', 've'];

/**
 * Decode a Blob to an AudioBuffer using a temporary AudioContext.
 * Closes the context when done to free resources.
 */
async function decodeBlob(blob: Blob): Promise<AudioBuffer> {
  const arrayBuffer = await blob.arrayBuffer();
  const tempCtx = new AudioContext();
  try {
    return await tempCtx.decodeAudioData(arrayBuffer);
  } finally {
    await tempCtx.close().catch(() => {});
  }
}

/**
 * Resample an AudioBuffer to a target sample rate using OfflineAudioContext.
 * Renders the full buffer non-interactively (no real-time constraint).
 */
async function resampleBuffer(decoded: AudioBuffer, targetSampleRate: number): Promise<AudioBuffer> {
  const numChannels = 1; // Always mono for speech
  const targetLength = Math.ceil(decoded.duration * targetSampleRate);
  const offlineCtx = new OfflineAudioContext(numChannels, targetLength, targetSampleRate);
  const source = offlineCtx.createBufferSource();
  source.buffer = decoded;
  source.connect(offlineCtx.destination);
  source.start(0);
  return offlineCtx.startRendering();
}

/**
 * Compress and chunk audio for upload.
 *
 * Strategy:
 *   1. Decode the full audio
 *   2. Pick the highest sample rate that keeps a single chunk ≤45MB (min 4kHz)
 *   3. If even 4kHz produces >45MB (>~160 min), split into ≤45-min chunks
 *   4. Return array of WAV Blobs — each guaranteed ≤45MB
 *
 * Handles Frans's meeting range: 45–120 min recordings.
 *   - 45 min at 8kHz = 41MB  ✓ (single chunk)
 *   - 90 min at 4kHz = 41MB  ✓ (single chunk)
 *   - 120 min at 4kHz = 55MB → 2 chunks × 27.5MB  ✓
 */
async function compressAudioForUpload(
  blob: Blob,
  onLog: (msg: string, type: 'info' | 'success' | 'warning') => void
): Promise<Blob[]> {
  onLog('Decoding audio for compression...', 'info');
  const decoded = await decodeBlob(blob);
  const durationS = decoded.duration;
  const durationMin = Math.floor(durationS / 60);

  // Pick sample rate: highest rate that fits entire recording in one 45MB chunk
  // bytes = duration_s * sampleRate * 2 (16-bit) → sampleRate = 45MB / (duration_s * 2)
  const bytesPerSamplePerSecond = 2; // 16-bit mono
  const idealRate = Math.floor(CHUNK_SIZE_LIMIT / (durationS * bytesPerSamplePerSecond));
  const targetSampleRate = Math.max(MIN_SAMPLE_RATE, Math.min(MAX_SAMPLE_RATE, idealRate));

  const projectedSizeMB = ((durationS * targetSampleRate * bytesPerSamplePerSecond) / (1024 * 1024)).toFixed(1);
  onLog(`Resampling ${durationMin}m audio to ${targetSampleRate / 1000}kHz mono (~${projectedSizeMB}MB projected)...`, 'info');

  const resampled = await resampleBuffer(decoded, targetSampleRate);

  // Check if single-chunk fits within limit
  const singleChunk = audioBufferToWav(resampled);
  if (singleChunk.size <= CHUNK_SIZE_LIMIT) {
    const sizeMB = (singleChunk.size / (1024 * 1024)).toFixed(1);
    onLog(`Audio compressed to ${sizeMB}MB (${targetSampleRate / 1000}kHz mono WAV)`, 'success');
    return [singleChunk];
  }

  // Still over 45MB (>~160 min recording) — split into chunks of ≤45 min each
  const chunkDurationS = 45 * 60; // 45 minutes per chunk
  const totalChunks = Math.ceil(durationS / chunkDurationS);
  onLog(`Recording too large for single upload — splitting into ${totalChunks} chunks of ≤45min...`, 'warning');

  const chunks: Blob[] = [];
  for (let i = 0; i < totalChunks; i++) {
    const startS = i * chunkDurationS;
    const endS = Math.min(startS + chunkDurationS, durationS);
    const chunkBlob = sliceAudioBufferToWav(resampled, startS, endS);
    const chunkMB = (chunkBlob.size / (1024 * 1024)).toFixed(1);
    onLog(`Chunk ${i + 1}/${totalChunks}: ${Math.floor(startS / 60)}–${Math.floor(endS / 60)}min (${chunkMB}MB)`, 'info');
    chunks.push(chunkBlob);
  }

  return chunks;
}

/**
 * Slice an AudioBuffer to a time range [startS, endS] and encode directly to WAV Blob.
 * Avoids creating an intermediate AudioBuffer (which requires OfflineAudioContext).
 */
function sliceAudioBufferToWav(buffer: AudioBuffer, startS: number, endS: number): Blob {
  const sampleRate = buffer.sampleRate;
  const startSample = Math.floor(startS * sampleRate);
  const endSample = Math.min(Math.floor(endS * sampleRate), buffer.length);
  const length = endSample - startSample;
  const src = buffer.getChannelData(0).subarray(startSample, endSample);

  const ab = new ArrayBuffer(44 + length * 2);
  const view = new DataView(ab);
  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, ab.byteLength - 8, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);        // PCM
  view.setUint16(22, 1, true);        // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, length * 2, true);
  let off = 44;
  for (let i = 0; i < src.length; i++) {
    const s = Math.max(-1, Math.min(1, src[i]));
    view.setInt16(off, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    off += 2;
  }
  return new Blob([ab], { type: 'audio/wav' });
}

/**
 * Base64-encode a (small, ≤~3MB) WAV Blob for the Gemini inline-data path.
 * Chunked over 32KB slices so String.fromCharCode never blows the arg limit.
 */
async function wavToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  const SLICE = 0x8000;
  for (let i = 0; i < bytes.length; i += SLICE) {
    binary += String.fromCharCode(...bytes.subarray(i, i + SLICE));
  }
  return btoa(binary);
}

/**
 * Sequential context-passing chunked Gemini transcription for long (>8 min)
 * Afrikaans recordings — the only path that yields clean Afrikaans at scale
 * (AssemblyAI-`af` is garbage; a single Gemini call 504s past ~8 min).
 *
 * Slices the audio into overlapping ~90s windows (16kHz mono WAV, base64 under
 * Vercel's body limit) and transcribes them IN ORDER, feeding each chunk the
 * previous chunk's tail (with speaker labels) + an instruction to keep speaker
 * numbering and skip the overlap. Gemini maintains continuity itself, so the
 * chunks concatenate after a simple per-chunk timestamp offset — no post-hoc
 * stitch / dedup / speaker reconciliation. Empirically validated 2026-06-01.
 *
 * Throws PIPELINE_CANCELLED (propagated by checkAborted) so the caller can
 * distinguish a user cancel from a genuine engine failure.
 *
 * `fallbackCall` is the caller's guarded non-Gemini route. For Afrikaans it
 * always throws a truthful "temporarily unavailable" error (engineRouting forbids
 * AssemblyAI/Deepgram/Groq for 'af'); it only returns a transcript for languages
 * the routing module allows.
 * If the Gemini circuit breaker opens mid-recording — an auth failure trips
 * it on the very first occurrence, see recordGeminiFailure() — the remaining
 * chunks (and the chunk that tripped it) are NOT retried against a dead key
 * and NOT papered over with gap markers; the whole recording is handed to
 * `fallbackCall` instead, once. A degraded-but-complete AssemblyAI transcript
 * beats a Gemini transcript that is mostly "[inaudible]" because Google
 * rejected every remaining call the same way.
 */
async function transcribeAfrikaansChunkedSequential(
  sourceBlob: Blob,
  service: TranscriptionService,
  onLog: LogCallback,
  checkAborted: () => void,
  fallbackCall: () => Promise<TranscribeResult>,
): Promise<TranscribeResult> {
  onLog('Decoding audio for chunked Afrikaans transcription…', 'info');
  const decoded = await decodeBlob(sourceBlob);
  const resampled = await resampleBuffer(decoded, CHUNK_SAMPLE_RATE);
  const totalDuration = resampled.duration;

  const windows = computeChunkWindows(totalDuration);
  onLog(
    `Chunked Afrikaans: ${windows.length} window(s) of ~${CHUNK_WINDOW_S}s ` +
      `(${CHUNK_OVERLAP_S}s overlap) at ${CHUNK_SAMPLE_RATE / 1000}kHz…`,
    'info',
  );

  const allUtterances: TranscriptionUtterance[] = [];
  let prevTail = '';
  let failedChunks = 0;

  const { usedFallback, result: fallbackResult } = await runChunkedGeminiWithBreaker<TranscribeResult>({
    windowCount: windows.length,
    isBreakerOpen: () => geminiCircuitBreaker.isOpen(),
    transcribeChunk: async (i) => {
      checkAborted();
      const win = windows[i];
      const wavBlob = sliceAudioBufferToWav(resampled, win.start, win.end);
      const base64 = await wavToBase64(wavBlob);
      const contextHint = win.index === 0 ? undefined : buildContinuationHint(prevTail);
      const minSeg = minPlausibleSegments(win.end - win.start);

      onLog(
        `Transcribing chunk ${win.index + 1}/${windows.length} ` +
          `(${Math.floor(win.start)}–${Math.floor(win.end)}s)…`,
        'info',
      );

      // Per-chunk retry: a long recording fires dozens of independent calls, and a
      // single transient 504 (FUNCTION_INVOCATION_TIMEOUT) or 422 (per-chunk
      // repetition) must retry THIS chunk — not abort the whole transcript. The
      // service treats 504/422 as non-retryable (correct for whole-file calls),
      // so the retry lives here (pure, unit-tested in chunkAudio.ts).
      return transcribeChunkWithRetry<TranscribeResult>({
        attempt: async () => {
          // The breaker may have tripped on THIS chunk's previous attempt
          // (auth failure) — don't spend another round-trip on a key Google
          // has already rejected; fail fast so chunkAudio's retry loop only
          // burns backoff time, not network calls.
          if (geminiCircuitBreaker.isOpen()) {
            throw new GeminiUnavailableError('assemblyai', 'auth');
          }
          try {
            const r = await service.transcribeWithGemini(base64, 'audio/wav', onLog, contextHint);
            geminiCircuitBreaker.recordSuccess();
            return r;
          } catch (err) {
            recordGeminiFailure(err);
            throw err;
          }
        },
        segmentCount: (r) => r.utterances.length,
        minSegments: minSeg,
        onWarn: (m) => onLog(`Chunk ${win.index + 1}: ${m}`, 'warning'),
        checkAborted,
      });
    },
    onGap: (i) => {
      // Exhausted retries on a genuinely transient failure (breaker still
      // closed) — insert an honest gap so the timeline stays intact and the
      // rest of the meeting is still usable. Clear the tail so the next
      // chunk isn't fed stale continuation context across the gap.
      const win = windows[i];
      failedChunks++;
      allUtterances.push({
        start: win.start,
        end: win.end,
        transcript: gapMarkerText(),
        speaker: 0,
        confidence: 0,
        words: [],
      });
      prevTail = '';
    },
    onChunkResult: (i, chunk) => {
      const win = windows[i];
      // Offset chunk-local timestamps to global recording time, then append.
      for (const u of chunk.utterances) {
        allUtterances.push({
          ...u,
          start: u.start + win.start,
          end: u.end + win.start,
          words: u.words.map((w) => ({ ...w, start: w.start + win.start, end: w.end + win.start })),
        });
      }
      prevTail = renderTailContext(chunk.utterances);
    },
    fallbackCall,
  });

  if (usedFallback) {
    onLog(
      'Gemini circuit breaker open (auth failure) — completed the rest of this recording via the fallback engine instead of leaving gaps.',
      'warning',
    );
    // fallbackResult is always non-null when usedFallback is true.
    return fallbackResult as TranscribeResult;
  }

  // Fail loud if the engine was mostly down — never present a transcript that is
  // more gap than content as a success (the user must be told to retry).
  if (tooManyChunksFailed(failedChunks, windows.length)) {
    throw new Error(
      `Afrikaans transcription failed: ${failedChunks}/${windows.length} segments could not be ` +
        `transcribed (engine likely temporarily unavailable). Please retry in a few minutes.`,
    );
  }
  if (allUtterances.length === 0) {
    throw new Error('Chunked Gemini produced no transcript segments.');
  }

  // Rebuild formatted + plain transcript from the concatenated utterances.
  const fmtTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };
  const formatted = allUtterances
    .map((u) => `[${fmtTime(u.start)}] **Speaker ${u.speaker + 1}:** ${u.transcript}`)
    .join('\n\n');
  const transcript = allUtterances.map((u) => u.transcript).join(' ');
  const speakers = new Set(allUtterances.map((u) => u.speaker)).size || 1;

  onLog(
    `Chunked Afrikaans complete: ${allUtterances.length} segments across ${windows.length} chunk(s).`,
    'success',
  );

  return {
    transcript,
    utterances: allUtterances,
    speakers,
    duration: totalDuration,
    engine: `${API_CONFIG.TRANSCRIBE_ENGINE_ID}-chunked`,
    language: 'af',
    confidence: 0.9,
    formatted,
  };
}

/**
 * Encode an AudioBuffer to a 16-bit PCM WAV Blob.
 * Mono only — speech transcription doesn't need stereo.
 */
function audioBufferToWav(buffer: AudioBuffer): Blob {
  const numChannels = 1;
  const sampleRate = buffer.sampleRate;
  const bitDepth = 16;
  const bytesPerSample = bitDepth / 8;
  const channelData = buffer.getChannelData(0);
  const dataLength = channelData.length * bytesPerSample;
  const totalLength = 44 + dataLength;

  const ab = new ArrayBuffer(totalLength);
  const view = new DataView(ab);

  // RIFF WAV header
  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, totalLength - 8, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);            // fmt chunk size
  view.setUint16(20, 1, true);             // PCM
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
  view.setUint16(32, numChannels * bytesPerSample, true);
  view.setUint16(34, bitDepth, true);
  writeStr(36, 'data');
  view.setUint32(40, dataLength, true);

  // PCM samples
  let offset = 44;
  for (let i = 0; i < channelData.length; i++) {
    const s = Math.max(-1, Math.min(1, channelData[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  return new Blob([ab], { type: 'audio/wav' });
}

// ── Helpers ───────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function buildSpeakerMap(speakerCount: number, utterances: TranscriptionUtterance[]): string {
  if (speakerCount === 0) return 'No speakers detected.';

  const lines: string[] = ['SPEAKER MAP:'];
  const stats: Record<number, { wordCount: number; firstSeen: number; lastSeen: number }> = {};

  for (const u of utterances) {
    if (!stats[u.speaker]) {
      stats[u.speaker] = { wordCount: 0, firstSeen: u.start, lastSeen: u.end };
    }
    stats[u.speaker].wordCount += u.words.length;
    stats[u.speaker].lastSeen = Math.max(stats[u.speaker].lastSeen, u.end);
  }

  // Iterate over the actual speaker IDs present in utterances (sorted) so that
  // non-contiguous IDs (e.g. 0 and 2 from Deepgram) don't silently drop speakers.
  const speakerIds = Object.keys(stats).map(Number).sort((a, b) => a - b);
  speakerIds.forEach((id, idx) => {
    const s = stats[id];
    lines.push(
      `* **Speaker ${idx + 1}:** ${s.wordCount} words spoken ` +
        `(first heard at ${formatTime(s.firstSeen)}, last at ${formatTime(s.lastSeen)})`
    );
  });

  return lines.join('\n');
}

// Circuit breaker state for Gemini fallback.
// After THRESHOLD consecutive Gemini failures, fall back to Deepgram for COOLDOWN_MS.
export const geminiCircuitBreaker = {
  failures: 0,
  lastFailure: 0,
  THRESHOLD: 5,             // Raised from 2 — iterative testing shouldn't lock out Gemini
  COOLDOWN_MS: 2 * 60 * 1000, // Reduced to 2 min — recover faster for retries
  isOpen(): boolean {
    if (this.failures < this.THRESHOLD) return false;
    // Auto-reset after cooldown
    if (Date.now() - this.lastFailure > this.COOLDOWN_MS) {
      this.failures = 0;
      return false;
    }
    return true;
  },
  recordFailure() {
    this.failures++;
    this.lastFailure = Date.now();
  },
  /**
   * Open the breaker immediately, bypassing THRESHOLD. Auth failures (Google
   * rejecting the API key outright) are not transient like a 504 — one is
   * enough evidence that every subsequent call will fail the same way, so
   * waiting for THRESHOLD consecutive failures would mean four more wasted
   * calls (or, per-chunk, dozens) against a dead key. See the 2026-09-16
   * 12:33Z outage: a customer hit ~90 dead Gemini calls in 30 minutes because
   * nothing tripped the breaker on the first rejection.
   */
  trip() {
    this.failures = this.THRESHOLD;
    this.lastFailure = Date.now();
  },
  recordSuccess() {
    this.failures = 0;
  },
};

/**
 * Whether a caught error represents Gemini rejecting the request for auth
 * reasons (never transient) rather than a generic/transient failure.
 * Covers both the typed GeminiUnavailableError (server already classified
 * it) and a raw 401/403 status surfacing from the /api/transcribe-gemini
 * route via ApiError.
 */
export function isGeminiAuthFailure(err: unknown): boolean {
  if (err instanceof GeminiUnavailableError) return true;
  const status = (err as { status?: number } | null)?.status;
  return status === 401 || status === 403;
}

/**
 * Feed a Gemini failure into the circuit breaker: auth failures open it
 * immediately (one is enough — see geminiCircuitBreaker.trip()), anything
 * else (transient 504s, quality-gate misses) only increments the counter so
 * THRESHOLD consecutive failures are still required before the breaker opens.
 */
export function recordGeminiFailure(err: unknown): void {
  if (isGeminiAuthFailure(err)) {
    geminiCircuitBreaker.trip();
  } else {
    geminiCircuitBreaker.recordFailure();
  }
}

/**
 * Drive N sequential chunk attempts, consulting `isBreakerOpen` before each
 * one. Once the breaker is open — already open going into a chunk, or
 * tripped mid-chunk by an auth failure (see recordGeminiFailure) — stop
 * spending further Gemini attempts on the remaining chunks (and the chunk
 * that just failed) and delegate the WHOLE recording to `fallbackCall` in a
 * single call, the same AssemblyAI path the hook already uses when Gemini is
 * skipped entirely. A degraded-but-complete transcript beats one that is
 * mostly gap markers because the engine is provably dead.
 *
 * Pure aside from the injected async callbacks — no Web Audio / network
 * dependency, so it is unit-testable directly.
 */
export async function runChunkedGeminiWithBreaker<T>(deps: {
  windowCount: number;
  isBreakerOpen: () => boolean;
  /** One chunk's transcription, already including its own internal retry. Returns null when every internal attempt was exhausted (transient failure). */
  transcribeChunk: (index: number) => Promise<T | null>;
  onGap: (index: number) => void;
  onChunkResult: (index: number, result: T) => void;
  fallbackCall: () => Promise<T>;
}): Promise<{ usedFallback: boolean; result: T | null }> {
  for (let i = 0; i < deps.windowCount; i++) {
    if (deps.isBreakerOpen()) {
      return { usedFallback: true, result: await deps.fallbackCall() };
    }

    const chunk = await deps.transcribeChunk(i);

    if (chunk === null) {
      if (deps.isBreakerOpen()) {
        return { usedFallback: true, result: await deps.fallbackCall() };
      }
      deps.onGap(i);
      continue;
    }

    deps.onChunkResult(i, chunk);
  }
  return { usedFallback: false, result: null };
}

// ── Hook ──────────────────────────────────────────────────────────────

export function useTranscription() {
  const serviceRef = useRef<TranscriptionService | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { decodeAudio, extractFrames, cleanup } = useAudioProcessing();

  const {
    setStep,
    setProgress,
    setSystemStatus,
    setError,
    addLog,
    setFinalTranscript,
    setIntelligence,
    setSpeakerMap,
    setSuggestedSpeakerNames,
    setVisualIdPending,
    setConfidence,
    setQualityGrade,
    setUtterances,
    setIntelligenceOutputs,
    setSavedTranscriptionId,
    setSceneDescriptions,
  } = useTranscriptionStore();

  const getService = useCallback((): TranscriptionService => {
    if (!serviceRef.current) {
      serviceRef.current = new TranscriptionService();
    }
    return serviceRef.current;
  }, []);

  /**
   * Process transcription result — set state, optionally generate intelligence
   */
  const handleResult = useCallback(
    async (
      result: TranscribeResult,
      directives: OutputDirectives,
      customInstructions: string,
      service: TranscriptionService,
      checkAborted: () => void,
      title?: string,
      userLanguage?: string,
      meetingLanguages?: string[],
      contentHash?: string,
    ) => {
      // Guard: reject empty transcripts — don't silently show "success" with nothing
      if (!result.formatted?.trim() && !result.transcript?.trim()) {
        throw new Error(
          `Transcription engine returned empty result (engine: ${result.engine}, ` +
          `duration: ${Math.floor(result.duration)}s, utterances: ${result.utterances?.length || 0}). ` +
          `The audio format may not be supported. Try converting to WAV or MP3 first.`
        );
      }

      setProgress(75);
      addLog(
        `Transcription complete: ${result.speakers} speaker${result.speakers !== 1 ? 's' : ''} detected, ` +
          `${Math.floor(result.duration)}s audio, engine: ${result.engine}`,
        'success'
      );

      setSpeakerMap(buildSpeakerMap(result.speakers, result.utterances));

      // ── Qwen3.5 4B fast-format pass ────────────────────────────────────
      // AssemblyAI Qwen3.5 4B (~600 ms, $0.10/M tokens) converts spoken
      // punctuation commands to symbols, normalises capitalisation and numbers,
      // and strips filler words at utterance boundaries.
      //
      // Non-blocking: errors fall back to the raw STT output silently.
      // Skipped automatically for transcripts > 50 000 chars (long meetings).
      // The Afrikaans Dutch-correction pass runs on top, independently.
      let refinedTranscript = result.formatted;
      if (result.formatted.length <= 50_000) {
        addLog('Applying Qwen3.5 4B fast-format pass…', 'info');
        refinedTranscript = await service.formatWithQwen(result.formatted, result.language);
        if (refinedTranscript !== result.formatted) {
          addLog('Qwen fast-format applied.', 'success');
        }
      }

      // ── Transcript refinement pass ──────────────────────────────────────
      // SPEC-031: Dutch→Afrikaans correction is applied automatically whenever
      // the STT engine returns language='af' or language='nl' (Deepgram nl-proxy).
      // No user toggle needed — verbatim Afrikaans is always corrected; English
      // and other languages are left entirely untouched.
      // Trigger the Dutch→Afrikaans correction pass when:
      //   • The user explicitly confirmed Afrikaans in the pre-transcription language prompt, OR
      //   • The STT engine itself returned 'af' or 'nl' as the detected language code.
      //
      // The user-selection path is the primary signal. Engine detection is a reliable
      // fallback for non-AssemblyAI engines that return per-utterance language codes.
      // For AssemblyAI + code-switching meetings, language_detection:true returns a single
      // dominant language_code that may be 'en' even when Afrikaans is heavily present —
      // the explicit user confirmation is what makes the correction pass reliable.
      const userSelectedAfrikaans = meetingLanguages?.includes('af') ?? false;
      const engineDetectedAfrikaans = result.language === 'af' || result.language === 'nl';
      const isAfrikaans = userSelectedAfrikaans || engineDetectedAfrikaans;
      if (isAfrikaans) {
        setStep(AppStep.REFINING);
        const refineReason = userSelectedAfrikaans && !engineDetectedAfrikaans
          ? `Afrikaans confirmed by user (engine detected '${result.language}')`
          : `engine detected: ${result.language}`;
        addLog(`Applying Dutch→Afrikaans correction pass (${refineReason})...`, 'info');
        // Chunk the refine over text windows so the ENTIRE transcript is corrected.
        // The refine endpoint truncates long input (refines the head, rule-corrects the
        // tail), which would leave most of a 50–180 min meeting un-refined. Splitting the
        // text is boundary-safe — AssemblyAI already locked speakers + timestamps, and
        // Dutch→Afrikaans correction is local to each segment, so windows need no overlap
        // or reconciliation. See docs/dtp-runs/2026-06-01-afrikaans-dutch-and-inp.md.
        const windows = splitRefineWindows(refinedTranscript);
        if (windows.length <= 1) {
          refinedTranscript = await service.refineTranscript(refinedTranscript);
        } else {
          addLog(`Long transcript — refining in ${windows.length} text windows…`, 'info');
          const refinedWindows: string[] = new Array(windows.length);
          let cursor = 0;
          const REFINE_CONCURRENCY = 4;
          const worker = async () => {
            while (cursor < windows.length) {
              const my = cursor++;
              refinedWindows[my] = await service.refineTranscript(windows[my]);
            }
          };
          await Promise.all(Array.from({ length: Math.min(REFINE_CONCURRENCY, windows.length) }, worker));
          refinedTranscript = refinedWindows.join('\n\n');
        }
      }
      // enforceLanguage directive removed (SPEC-031) — intelligence synthesis now
      // mandates British English output via system instruction, so no transcript
      // translation is needed. Verbatim transcript remains in original language.

      setFinalTranscript(refinedTranscript);
      setConfidence(result.confidence ?? null);
      setQualityGrade(result.quality_grade ?? null);
      setUtterances(result.utterances ?? null);
      setSceneDescriptions(result.sceneDescriptions?.length ? result.sceneDescriptions : null);

      // Intelligence synthesis (optional)
      const intelItems: string[] = [];
      if (directives.executiveSummary) intelItems.push('Produce an executive summary focusing on critical themes.');
      if (directives.meetingSentiment) intelItems.push('Audit interaction sentiment and speaker tone shifts.');
      if (directives.actionItems) intelItems.push('Extract precise action items and owners.');
      if (directives.speakerTone) intelItems.push('Perform tone analysis for all speakers.');

      if (intelItems.length > 0 || customInstructions.trim()) {
        setStep(AppStep.INTELLIGENCE_GEN);
        setProgress(85);
        checkAborted();
        addLog('Generating intelligence synthesis...', 'info');

        // Intelligence synthesis is OPTIONAL — its failure (e.g. a 408 timeout on a long
        // 50–180 min meeting) must NOT discard the verbatim transcript, which is already
        // set above (line ~392). Fixed 2026-06-01: this previously threw and aborted the
        // whole pipeline, so a synthesis timeout reported "transcript failed" and lost the
        // transcript entirely. See docs/dtp-runs/2026-06-01-afrikaans-dutch-and-inp.md.
        let intel = '';
        try {
          intel = await service.generateIntelligence(
            refinedTranscript,   // use corrected transcript for intelligence synthesis
            intelItems.join('\n') + '\n' + customInstructions,
            addLog
          );
          setIntelligence(intel);
          addLog('Intelligence synthesis complete.', 'success');
        } catch (intelErr: unknown) {
          if (intelErr instanceof Error && intelErr.message === 'PIPELINE_CANCELLED') throw intelErr;
          const _m = intelErr instanceof Error ? intelErr.message : String(intelErr);
          addLog(`Intelligence synthesis unavailable (${_m.slice(0, 80)}) — verbatim transcript is ready; enhanced reports may still generate below.`, 'warning');
        }

        // SPEC-034: Auto-apply speaker names extracted from intelligence text.
        // Intelligence synthesis routinely outputs "Speaker N (Name)" — e.g.
        // "Speaker 1 (David), Speaker 2 (Henk)" — parse those and replace
        // generic labels in the transcript and speakerMap so the DOCX export
        // shows real names without requiring manual entry or video frames.
        const intelNameMap: Record<number, string> = {};
        const intelNameRe = /\bSpeaker\s+(\d+)\s*\(([^)]{2,40})\)/gi;
        let intelMatch: RegExpExecArray | null;
        while ((intelMatch = intelNameRe.exec(intel)) !== null) {
          const num = parseInt(intelMatch[1], 10);
          const name = intelMatch[2].trim();
          if (name && num > 0) intelNameMap[num] = name;
        }

        if (Object.keys(intelNameMap).length > 0) {
          // Dedup guard: if the same name maps to multiple speaker numbers,
          // skip it — likely a template artefact, not a real identity.
          const intelNameCounts = new Map<string, number>();
          for (const n of Object.values(intelNameMap)) {
            intelNameCounts.set(n, (intelNameCounts.get(n) ?? 0) + 1);
          }

          // Sort descending so "Speaker 10" is replaced before "Speaker 1".
          const sortedIntelNums = Object.keys(intelNameMap)
            .map(Number)
            .sort((a, b) => b - a);

          let intelUpdatedMap = useTranscriptionStore.getState().speakerMap;
          for (const num of sortedIntelNums) {
            const name = intelNameMap[num];
            if (!name || (intelNameCounts.get(name) ?? 0) > 1) continue;
            refinedTranscript = refinedTranscript.split(`**Speaker ${num}**`).join(`**${name}**`);
            refinedTranscript = refinedTranscript.split(`Speaker ${num}`).join(name);
            intelUpdatedMap = intelUpdatedMap.split(`**Speaker ${num}**`).join(`**${name}**`);
            intelUpdatedMap = intelUpdatedMap.split(`Speaker ${num}`).join(name);
          }
          setFinalTranscript(refinedTranscript);
          setSpeakerMap(intelUpdatedMap);
          addLog(`Speaker names applied from intelligence: ${Object.values(intelNameMap).join(', ')}`, 'info');
        }
      }

      // SPEC-014: per-type enhanced intelligence (runs in parallel)
      type IntelTypeDef = { directive: keyof OutputDirectives; action: string; label: string; outputFormat?: 'json' | 'markdown' };
      const newIntelTypes: IntelTypeDef[] = [
        { directive: 'meetingMinutes',    action: 'meeting-minutes',    label: 'meeting minutes' },
        { directive: 'keyDecisions',      action: 'key-decisions',      label: 'key decisions' },
        { directive: 'followupEmail',     action: 'followup-email',     label: 'follow-up email' },
        { directive: 'actionItemsV2',     action: 'action-items-v2',    label: 'action items (v2)' },
        { directive: 'multilingualSummary', action: 'multilingual-summary', label: 'multilingual summary', outputFormat: 'markdown' },
      ];
      const enabledNewTypes = newIntelTypes.filter(t => directives[t.directive]);
      if (enabledNewTypes.length > 0) {
        setStep(AppStep.INTELLIGENCE_GEN);
        checkAborted();
        addLog(`Generating ${enabledNewTypes.length} enhanced intelligence report(s)...`, 'info');
        const typeResults = await Promise.allSettled(
          enabledNewTypes.map(({ action, label, outputFormat }) =>
            service.generateIntelligenceType(refinedTranscript, action, outputFormat)
              .then(r => ({ action, text: r.text || '', label }))
          )
        );
        const newOutputs: Record<string, string> = {};
        for (const r of typeResults) {
          if (r.status === 'fulfilled' && r.value.text) {
            newOutputs[r.value.action] = r.value.text;
          }
        }
        if (Object.keys(newOutputs).length > 0) {
          setIntelligenceOutputs(newOutputs);
          addLog(`Enhanced intelligence ready (${Object.keys(newOutputs).join(', ')}).`, 'success');
        }
      }

      // Save transcription to DB
      const session = useAuthStore.getState().session;
      if (session?.access_token) {
        try {
          addLog('Saving transcription...', 'info');
          // Charge-once: the server dedupes on (user, contentHash, language, 24 h) and,
          // for a long AssemblyAI job, updates that job's 'processing' stub in place.
          const saveRes = await postSaveTranscription(session.access_token, {
            title: title || undefined,
            transcript: refinedTranscript,   // store corrected version
            intelligence: useTranscriptionStore.getState().intelligence || null,
            // SPEC-034: use the store's speakerMap — it now has real names if
            // intelligence synthesis identified them (rather than rebuilding
            // generic "Speaker N" labels from scratch).
            speaker_map: useTranscriptionStore.getState().speakerMap || null,
            duration_seconds: result.duration,
            engine: result.engine,
            confidence: result.confidence,
            quality_grade: result.quality_grade ?? null,
            utterances: result.utterances ?? null,
            language: result.language || userLanguage || null,
            contentHash,
            assemblyaiId: result.assemblyaiId,
          });
          if (saveRes.ok) {
            const saveData = await saveRes.json();
            // Capture transcription ID for SPEC-014 intelligence-save + regenerate
            if (saveData.transcriptionId) {
              setSavedTranscriptionId(saveData.transcriptionId);
              // Best-effort: persist per-type intelligence outputs to DB
              const currentOutputs = useTranscriptionStore.getState().intelligenceOutputs;
              if (Object.keys(currentOutputs).length > 0) {
                void service.saveIntelligence(saveData.transcriptionId, currentOutputs);
              }
            }
            useAuthStore.getState().refreshBalance();
            if (saveData.charged === false) addLog('This file was already transcribed in the last 24 hours — no second charge.', 'info');
            const balMin = Math.floor((saveData.balance_seconds ?? 0) / 60);
            const balDisplay = saveData.balance_seconds >= 999_990
              ? 'Pro — unlimited'
              : `${balMin}m remaining`;
            addLog(`Saved. ${balDisplay}`, 'success');
          } else {
            // Surface the actual error so it appears in the processing log
            let errMsg = `HTTP ${saveRes.status}`;
            try { const body = await saveRes.json(); errMsg = body.error || errMsg; } catch { /* ignore */ }
            addLog(`Warning: DB save failed — ${errMsg}`, 'warning');
            console.error('save-transcription API error:', errMsg);
          }
        } catch (saveErr) {
          const msg = saveErr instanceof Error ? saveErr.message : String(saveErr);
          addLog(`Warning: DB save failed — ${msg}`, 'warning');
          console.error('save-transcription network error:', msg);
        }
      }

      setProgress(100);
      setSystemStatus(SYSTEM_STATUS.NOMINAL);
      setStep(AppStep.COMPLETED);
      addLog(`Done. Confidence: ${(result.confidence * 100).toFixed(1)}%`, 'success');
      await ForensicAudio.playSuccess();
    },
    [setProgress, addLog, setSpeakerMap, setFinalTranscript, setStep, setIntelligence, setSystemStatus, setConfidence, setQualityGrade, setUtterances, setIntelligenceOutputs, setSavedTranscriptionId, setSceneDescriptions]
  );

  /**
   * Execute the transcription pipeline
   */
  const executePipeline = useCallback(
    async (options: TranscriptionOptions): Promise<void> => {
      const { file, originalFile, remoteUrl, inputType, directives, customInstructions, title, meetingLanguages } = options;

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      const checkAborted = () => {
        if (signal.aborted) throw new Error('PIPELINE_CANCELLED');
      };

      if (!file && !remoteUrl) return;

      // ── STT language hint ───────────────────────────────────────────────
      // The per-recording LanguageSelectModal selection is the authority; it
      // falls back to the profile default. Afrikaans MUST be forced on the STT
      // engine (not left to auto-detection) or AssemblyAI resolves Afrikaans to
      // Dutch and emits Dutch lexis. See docs/dtp-runs/2026-06-01-afrikaans-dutch-and-inp.md.
      // Returns undefined only when nothing is known → engine auto-detects.
      const _profileDefaultLang = useAuthStore.getState().profile?.default_language;
      const sttLanguage: string | undefined = (() => {
        if (meetingLanguages && meetingLanguages.length > 0) {
          if (meetingLanguages.includes('af')) return 'af';   // force Afrikaans (alone or with English)
          if (meetingLanguages.includes('en')) return 'en';
          return meetingLanguages[0];
        }
        // Profile default is a concrete language code or undefined (never 'auto' per its type).
        // buildAssemblyParams treats any non-concrete value as auto-detection downstream.
        return _profileDefaultLang ?? undefined;
      })();

      // Path 4 guard: with no language chosen, AssemblyAI auto-detect resolves Afrikaans
      // to Dutch. If detection lands on nl/af we cannot know which it really was, so
      // refuse to save/charge and ask the user to pick explicitly.
      const rejectUndetectableDutchAfrikaans = (r: TranscribeResult): void => {
        if (isUndetectableDutchAfrikaans(sttLanguage, r.language, r.engine)) {
          throw new Error(
            'We detected Dutch/Afrikaans. Please choose Afrikaans or Dutch in the language picker and run again — you have not been charged.',
          );
        }
      };

      try {
        setSystemStatus(SYSTEM_STATUS.PROCESSING);
        // Fire-and-forget the cosmetic start sound — creating its AudioContext is a
        // main-thread cost that must not sit on the pipeline's critical path (INP).
        void ForensicAudio.playStart();
        setError(null);
        setProgress(2);

        // Inject auth token into service
        const service = getService();
        const session = useAuthStore.getState().session;
        service.setAuthToken(session?.access_token ?? null);

        // Balance check (skip if no auth — e.g. dev mode)
        if (session?.access_token) {
          try {
            const balRes = await fetch('/api/profile/check-balance', {
              headers: { 'Authorization': `Bearer ${session.access_token}` },
            });
            if (balRes.ok) {
              const { balance_seconds } = await balRes.json();
              // Guard: null/undefined balance (new profile row) is treated as non-blocking.
              // Without this check, null <= 0 evaluates to true (null coerces to 0 in JS),
              // incorrectly triggering NO_BALANCE for users whose profile row is still being created.
              if (typeof balance_seconds === 'number' && balance_seconds <= 0) {
                setError('NO_BALANCE');
                setSystemStatus(SYSTEM_STATUS.ERROR);
                setStep(AppStep.ERROR);
                return;
              }
            } else {
              // Non-2xx from balance API (e.g. 401 token expiry, 500 DB issue) —
              // log a warning and proceed rather than blocking the user.
              addLog(`Balance check returned ${balRes.status} — proceeding with caution.`, 'warning');
            }
          } catch {
            // Network failure — non-blocking, proceed
            addLog('Could not verify balance. Proceeding...', 'warning');
          }
        }

        // Content hash of the ORIGINAL upload, computed once before any engine call. It is the
        // server-side charge-once key (same user + file + language within 24 h = one charge).
        // undefined (URL input / no SubtleCrypto) → today's behaviour, no dedupe.
        const hashSource = originalFile ?? file;
        const contentHash = hashSource ? await sha256Hex(hashSource) : undefined;

        const useGemini = !geminiCircuitBreaker.isOpen();
        // Large files use AssemblyAI regardless of circuit breaker state
        addLog('Initialising transcription pipeline...', 'info');

        // Quality check: detect bad Gemini transcripts (repetitive or sparse)
        // Mirrors the server-side detectSparseOutput gate in api/transcribe-gemini.ts
        const isBadTranscript = (result: TranscribeResult): string | null => {
          const lines = result.formatted.split('\n').filter(l => l.trim());

          // Check 1: repetitive lines
          if (lines.length >= 6) {
            const textOnly = lines.map(l => l.replace(/^\[\d+:\d+\]\s*\*\*.*?\*\*:?\s*/, '').trim().toLowerCase());
            const counts = new Map<string, number>();
            for (const t of textOnly) {
              counts.set(t, (counts.get(t) || 0) + 1);
            }
            for (const [, count] of counts) {
              if (count / lines.length > 0.4 && count >= 5) {
                return `Repetitive output detected (${count}/${lines.length} identical lines)`;
              }
            }
          }

          // Check 2: segment count — expect ≥1 segment per 30s (matches server gate)
          if (result.duration > 30) {
            const minExpected = Math.max(2, Math.floor(result.duration / 30));
            if (result.utterances.length < minExpected) {
              return `Only ${result.utterances.length} segment(s) for ~${Math.floor(result.duration)}s audio (expected ≥${minExpected})`;
            }
          }

          // Check 3: word density — real speech ≥20 wpm minimum (matches server gate)
          if (result.duration > 60) {
            const totalWords = result.utterances.reduce(
              (sum, u) => sum + u.words.length, 0
            );
            const wpm = (totalWords / result.duration) * 60;
            if (wpm < 20) {
              return `Low word density: ${totalWords} words for ~${Math.floor(result.duration)}s (${wpm.toFixed(0)} wpm, expected ≥20)`;
            }
          }

          // Check 4: summary/meta-descriptions instead of actual transcription
          for (const u of result.utterances) {
            if (/\[repeatedly|\[repeats|\[continues|\[same phrase|\[inaudible for|\[silence|\[background/i.test(u.transcript)) {
              return `Contains description instead of transcription: "${u.transcript.slice(0, 60)}"`;
            }
          }

          return null; // transcript looks OK
        };

        // Helper: attempt Gemini, fall back to Deepgram on failure.
        // NOTE: Groq Whisper CANNOT transcribe Afrikaans — it hallucinates English.
        // If Gemini fails and the Deepgram fallback also fails, we surface a clear
        // "retry Gemini" error rather than silently returning Groq gibberish.
        const transcribeWithFallback = async (
          geminiCall: () => Promise<TranscribeResult>,
          fallbackCall: () => Promise<TranscribeResult>
        ): Promise<TranscribeResult> => {
          if (!useGemini) return fallbackCall();

          let geminiReason = '';
          try {
            const result = await geminiCall();
            // Client-side quality gate
            const qualityIssue = isBadTranscript(result);
            if (qualityIssue) {
              geminiReason = qualityIssue;
              addLog(`Gemini quality issue: ${qualityIssue}. Checking for a permitted fallback engine...`, 'warning');
              geminiCircuitBreaker.recordFailure();
            } else {
              geminiCircuitBreaker.recordSuccess();
              return result;
            }
          } catch (geminiErr: unknown) {
            geminiReason = geminiErr instanceof Error ? geminiErr.message : String(geminiErr);
            addLog(`Gemini failed: ${geminiReason}. Checking for a permitted fallback engine...`, 'warning');
            // Auth failures (GeminiUnavailableError / 401 / 403) open the breaker
            // immediately — they can't succeed by retrying — so THIS request's
            // fallback below is not the only benefit: every subsequent call in
            // the session skips Gemini too. See recordGeminiFailure().
            recordGeminiFailure(geminiErr);
          }

          // Attempt Deepgram fallback
          try {
            addLog('Trying fallback engine...', 'info');
            const fallbackResult = await fallbackCall();

            // Validate fallback result — Groq Whisper produces hallucinated English
            // for Afrikaans audio. If the result engine is Groq AND word density is
            // very low, reject it and throw so the user retries with Gemini.
            if (fallbackResult.engine.includes('groq')) {
              const totalWords = fallbackResult.utterances.reduce((sum, u) => sum + u.words.length, 0);
              const wpm = fallbackResult.duration > 0 ? (totalWords / fallbackResult.duration) * 60 : 999;
              if (wpm < 20) {
                throw new Error(
                  `Groq Whisper produced insufficient output (${wpm.toFixed(0)} wpm) — ` +
                  `likely an Afrikaans/multilingual recording. Groq does not support Afrikaans. ` +
                  `Please retry — Gemini 2.5 Flash will handle this file.`
                );
              }
              // Also warn the user that Groq doesn't support Afrikaans well
              addLog('⚠ Groq Whisper does not support Afrikaans — transcript may be inaccurate. Retry to use Gemini.', 'warning');
            }

            return fallbackResult;
          } catch (fallbackErr: unknown) {
            const fallbackMsg = fallbackErr instanceof Error ? fallbackErr.message : String(fallbackErr);
            // Both engines failed — give a clear combined error
            throw new Error(
              `Both transcription engines failed.\n` +
              `• Gemini: ${geminiReason}\n` +
              `• Fallback engine: ${fallbackMsg}\n` +
              `Try again in a few minutes. You have not been charged.`
            );
          }
        };

        // ── URL input — pass directly to server ────────────────────
        if (inputType === 'url' && remoteUrl) {
          setStep(AppStep.FETCHING);
          addLog('Fetching remote audio file...', 'info');
          checkAborted();

          setStep(AppStep.TRANSCRIBING);
          setProgress(15);
          addLog('Sending to transcription engine...', 'info');

          const _profile = useAuthStore.getState().profile;
          const _lang = (_profile?.default_language ?? 'auto') as string;
          const _diarize = _profile?.default_diarise ?? true;
          const _rawEng = _profile?.default_engine ?? 'auto';
          const _engine = (_rawEng === 'assemblyai' ? 'auto' : _rawEng) as 'auto' | 'deepgram' | 'groq';
          // Afrikaans/SA-indigenous never fall back to AssemblyAI/Deepgram/Groq (see engineRouting).
          // SA-indigenous (zu/xh/st/tn/ve) run on Gemini ONLY — AssemblyAI/Deepgram
          // produce garbage for them (like Afrikaans→Dutch), so we pass a verbatim
          // language hint and fail loud rather than "succeed" with mistranscription. (beta)
          const _saLang = GEMINI_ONLY_LANGS.includes(sttLanguage ?? '') ? sttLanguage : undefined;
          // SA-indigenous: ElevenLabs Scribe FIRST (validated ~3× more accurate
          // than Gemini on isiXhosa, 2026-06-27); on Scribe failure (error/empty)
          // FALL BACK to the Gemini path (which has its do-not-translate hint +
          // self-correcting retry). The leakage guard below still runs as a
          // cross-check on the final transcript.
          const _effLang = sttLanguage ?? (_lang === 'auto' ? undefined : _lang);
          const _urlPrimary = _saLang
            ? () => service.transcribeFromUrlWithElevenLabs(remoteUrl, addLog, _saLang)
            : () => service.transcribeFromUrlWithGemini(remoteUrl, addLog, sttLanguage);
          const _urlFallback = _saLang
            ? () => service.transcribeFromUrlWithGemini(remoteUrl, addLog, _saLang)
            : () => {
                // Routing rules live in engineRouting: a restricted language (af, SA-indigenous)
                // has no permitted non-Gemini fallback, so fail truthfully (nothing saved → no charge).
                if (!isEngineAllowed(_effLang, 'deepgram')) throw new Error(engineUnavailableMessage(_effLang));
                assertEngineAllowed(_effLang, 'deepgram');
                return service.transcribeFromUrl(remoteUrl, addLog, { engine: _engine, language: _effLang ?? _lang, diarize: _diarize });
              };
          const result = await transcribeWithFallback(_urlPrimary, _urlFallback);

          checkAborted();
          // SA-indigenous beta guard: warn if Gemini appears to have translated to
          // English rather than transcribed in-language (never silently pass it off).
          if (_saLang && looksLikeEnglishTranslation(result.transcript).flagged) {
            addLog(`⚠ This ${_saLang.toUpperCase()} transcription (beta) looks like it may have been translated to English rather than transcribed in-language. Please verify the output.`, 'warning');
          }
          rejectUndetectableDutchAfrikaans(result);
          await handleResult(result, directives, customInstructions, service, checkAborted, title, _lang, meetingLanguages, contentHash);
          return;
        }

        // ── File input ─────────────────────────────────────────────
        const sourceBlob = file;
        if (!sourceBlob) throw new Error('No audio file provided.');

        // Phase 1: Decode audio for duration info
        setStep(AppStep.UPLOADING);
        checkAborted();
        setProgress(10);
        addLog('Decoding audio file...', 'info');

        const { duration } = await decodeAudio(sourceBlob);
        addLog(`Audio loaded: ${Math.floor(duration)}s duration`, 'success');
        // Pay-as-you-go minimum: repeat the upload notice in the log once the true length is known.
        const floorProfile = useAuthStore.getState().profile;
        const floor = paygFloorUpload({
          plan: floorProfile?.plan,
          durationSeconds: duration,
          balanceSeconds: floorProfile?.balance_seconds,
          legacySeconds: floorProfile?.legacy_paid_seconds,
        });
        if (floor.message) addLog([floor.message, floor.lowBalance].filter(Boolean).join(' '), 'warning');

        // Determine upload strategy based on file size
        setProgress(15);
        const mimeType = sourceBlob.type || 'audio/wav';
        const fileSizeMB = (sourceBlob.size / (1024 * 1024)).toFixed(1);
        let result: TranscribeResult;

        if (sourceBlob.size > DIRECT_UPLOAD_LIMIT) {
          // SA-indigenous (beta) is Gemini-only and not yet wired into the large-file /
          // chunked path — fail loud with guidance rather than routing to AssemblyAI,
          // which mistranscribes these languages. Phase 2: chunked SA path like Afrikaans.
          if (GEMINI_ONLY_LANGS.includes(sttLanguage ?? '')) {
            throw new Error(`${(sttLanguage ?? '').toUpperCase()} transcription is in beta and currently supports shorter recordings only (under ~4MB). Please upload a shorter clip.`);
          }
          // ── Large file: upload to Supabase Storage → AssemblyAI Universal-2 ──
          //
          // AssemblyAI accepts the Supabase signed URL directly.
          // No Gemini Files API upload, no chunking — 5GB file limit, 10-hour audio limit.
          // Handles Afrikaans + English code-switching natively (Universal-2 + Universal-3-Pro).
          //
          // Compression is only needed if the file exceeds the 50MB Supabase bucket cap.
          let uploadBlob: Blob = sourceBlob;
          let uploadMime = mimeType;

          if (sourceBlob.size > STORAGE_FILE_LIMIT) {
            const compressSource = originalFile || sourceBlob;
            try {
              // compressAudioForUpload returns an array; for AssemblyAI we only need one file.
              // Dynamic rate ensures the single WAV fits ≤45MB for meetings up to ~160 min.
              // For >160 min, it will chunk — but for 45-120 min meetings it's always one chunk.
              const chunks = await compressAudioForUpload(compressSource, addLog);
              uploadBlob = chunks[0]; // First (usually only) chunk
              uploadMime = 'audio/wav';
              if (chunks.length > 1) {
                addLog(`⚠ Recording exceeds 160 min — using first ${Math.floor(45)}min segment. Contact support for longer recordings.`, 'warning');
              }
            } catch (compErr: unknown) {
              if (originalFile && originalFile.size <= STORAGE_FILE_LIMIT) {
                addLog('Audio compression unavailable. Using original video file.', 'warning');
                uploadBlob = originalFile;
                uploadMime = originalFile.type || 'video/mp4';
              } else {
                const origMB = originalFile ? (originalFile.size / (1024 * 1024)).toFixed(1) : fileSizeMB;
                throw new Error(
                  `File too large for upload (${origMB}MB, max 50MB). ` +
                  'Try extracting audio with FFmpeg: ffmpeg -i input.mp4 -ac 1 -ar 8000 output.wav'
                );
              }
            }
          }

          // Upload to Supabase Storage
          const userId = session?.user?.id || 'anon';
          const ext = uploadMime.includes('video') ? 'mp4' : 'wav';
          const fileName = `${userId}/transcribe/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
          const uploadSizeMB = (uploadBlob.size / (1024 * 1024)).toFixed(1);

          addLog(`Uploading ${uploadSizeMB}MB to storage...`, 'info');
          const { error: uploadError } = await supabase.storage
            .from('lp-audio')
            .upload(fileName, uploadBlob, { contentType: uploadMime, upsert: false });

          if (uploadError) throw new Error(`Storage upload failed: ${uploadError.message}`);

          const { data: urlData, error: urlError } = await supabase.storage
            .from('lp-audio')
            .createSignedUrl(fileName, 3600);

          if (urlError || !urlData?.signedUrl) {
            throw new Error(`Failed to create signed URL: ${urlError?.message || 'unknown'}`);
          }

          setStep(AppStep.TRANSCRIBING);
          setProgress(30);
          checkAborted();

          // AssemblyAI (→ Deepgram) path — the engine for non-Afrikaans large files and
          // the fallback for Afrikaans files too long for Gemini. sttLanguage forces
          // AssemblyAI's language_code (e.g. 'af') instead of auto-detection, which
          // resolves Afrikaans to Dutch (DTP-002 2026-06-01).
          const assemblyAIThenDeepgram = async (): Promise<TranscribeResult> => {
            assertEngineAllowed(sttLanguage, 'assemblyai');
            addLog(`Engine: AssemblyAI Universal-3 Pro / Universal-2 | Language: ${sttLanguage ?? 'auto-detect'} | Speaker diarisation`, 'info');
            try {
              return await service.transcribeFromUrlWithAssemblyAI(urlData.signedUrl, addLog, sttLanguage, { contentHash, signal });
            } catch (aaiErr: unknown) {
              // Still running after the polling budget is NOT a failure: falling back would transcribe
              // (and charge) the same file a second time. Surface the truthful message instead.
              if (aaiErr instanceof TranscriptionStillProcessingError || aaiErr instanceof TranscriptionFailedError || (aaiErr instanceof Error && aaiErr.message === 'PIPELINE_CANCELLED')) throw aaiErr;
              // AssemblyAI unavailable (API key not configured or service error) — fall back to Deepgram URL mode
              const _aaiMsg = aaiErr instanceof Error ? aaiErr.message : String(aaiErr);
              addLog(`AssemblyAI unavailable — falling back to Deepgram (${_aaiMsg.slice(0, 80)})`, 'warning');
              const _profile = useAuthStore.getState().profile;
              const _lang = (_profile?.default_language ?? 'auto') as string;
              const _diarize = _profile?.default_diarise ?? true;
              const _rawEng2 = _profile?.default_engine ?? 'auto';
              const _engine = (_rawEng2 === 'assemblyai' ? 'auto' : _rawEng2) as 'auto' | 'deepgram' | 'groq';
              assertEngineAllowed(sttLanguage, 'deepgram');
              assertEngineAllowed(sttLanguage, 'groq');
              return await service.transcribeFromUrl(urlData.signedUrl, addLog, { engine: _engine, language: sttLanguage ?? _lang, diarize: _diarize });
            }
          };

          // The ONLY route into assemblyAIThenDeepgram. A language the routing module
          // forbids on AssemblyAI/Deepgram/Groq (Afrikaans → Dutch drift) is NEVER
          // rerouted: surface a truthful error instead. Nothing is saved, so no charge.
          const fallbackToNonGemini = async (): Promise<TranscribeResult> => {
            if (!isEngineAllowed(sttLanguage, 'assemblyai')) {
              throw new Error(engineUnavailableMessage(sttLanguage));
            }
            return assemblyAIThenDeepgram();
          };

          // ── Video file: Gemini native video understanding (transcript + scene descriptions) ──
          // Only when the upload is still a video MIME type (not compressed to WAV) and
          // the file is short enough for Gemini's 60s generation window.
          if (uploadMime.startsWith('video/') && useGemini && duration <= GEMINI_SINGLE_CALL_MAX_S) {
            try {
              addLog(`Engine: Gemini 2.5 Flash (video) | uploading ${(uploadBlob.size / 1024 / 1024).toFixed(1)}MB for visual analysis…`, 'info');
              const up = await service.uploadToGemini(urlData.signedUrl, uploadMime, addLog);
              const vResult = await service.transcribeVideoFromGeminiFileUri(up.fileUri, up.mimeType, addLog);
              void service.deleteGeminiFile(up.fileName).catch(() => {});
              if (vResult.sceneDescriptions?.length) {
                addLog(`Video analysis complete: ${vResult.sceneDescriptions.length} scene description(s) captured.`, 'success');
              }
              geminiCircuitBreaker.recordSuccess();
              result = vResult;
            } catch (vErr: unknown) {
              const _vMsg = vErr instanceof Error ? vErr.message : String(vErr);
              addLog(`Video transcription failed (${_vMsg.slice(0, 80)}). Falling back to audio engine…`, 'warning');
              recordGeminiFailure(vErr);
              result = await fallbackToNonGemini();
            }

          // Afrikaans engine choice (Gemini is the only engine that yields clean
          // Afrikaans — AssemblyAI-'af' is tier-3 garbage, Deepgram has no 'af'):
          //   • SHORT (≤8 min): Gemini 2.5 Flash via the Files-API split path (one
          //     generation call fits the 60s window); on failure/quality-miss → fail loud (no other engine for Afrikaans).
          //   • LONG (>8 min, the user's 50–180 min meetings): a single Gemini call blows
          //     Vercel's 60s window (→ 504), so transcribe as sequential context-passing
          //     chunks — overlapping ~90s windows, each fed the previous chunk's tail to
          //     keep speaker numbering + skip the overlap (validated 2026-06-01), with
          //     per-chunk retry + gap-tolerance. NO AssemblyAI fallback for Afrikaans
          //     (it mistranscribes to Dutch) — fail loud instead.
          // See docs/dtp-runs/2026-06-01-afrikaans-dutch-and-inp.md and
          //     docs/handoff/2026-06-01-afrikaans-long-meeting-chunked-gemini.md.
          } else if (sttLanguage === 'af' && useGemini && duration <= GEMINI_SINGLE_CALL_MAX_S) {
            try {
              addLog('Engine: Gemini 2.5 Flash (Afrikaans-optimised) | uploading via Files API…', 'info');
              const up = await service.uploadToGemini(urlData.signedUrl, uploadMime, addLog);
              const gResult = await service.transcribeFromGeminiFileUri(up.fileUri, up.mimeType, addLog);
              void service.deleteGeminiFile(up.fileName).catch(() => {});
              const qIssue = isBadTranscript(gResult);
              if (qIssue) {
                addLog(`Gemini quality issue: ${qIssue}. Afrikaans cannot be sent to another engine.`, 'warning');
                geminiCircuitBreaker.recordFailure();
                result = await fallbackToNonGemini();
              } else {
                geminiCircuitBreaker.recordSuccess();
                result = gResult;
              }
            } catch (gErr: unknown) {
              const _gMsg = gErr instanceof Error ? gErr.message : String(gErr);
              addLog(`Gemini failed (${_gMsg.slice(0, 80)}). Afrikaans cannot be sent to another engine.`, 'warning');
              recordGeminiFailure(gErr);
              result = await fallbackToNonGemini();
            }
          } else if (sttLanguage === 'af' && useGemini && duration > GEMINI_SINGLE_CALL_MAX_S) {
            // LONG Afrikaans (the user's 50–180 min meetings): a single Gemini call
            // 504s, so transcribe as sequential context-passing chunks with per-chunk
            // retry + gap-tolerance (validated 2026-06-01). We deliberately do NOT
            // treat a mere QUALITY-GATE miss (sparse/repetitive output) as a reason to
            // fall back to AssemblyAI: AssemblyAI-'af' is known Dutch garbage, so
            // "falling back" for a quality miss would manufacture the appearance of
            // success from garbage — exactly the failure the user reported. On an
            // unrecoverable quality miss we FAIL LOUD with an actionable message
            // instead. User cancel propagates unchanged.
            //
            // A dead Gemini KEY (auth failure — see recordGeminiFailure) is different:
            // transcribeAfrikaansChunkedSequential itself detects that (via the
            // circuit breaker, fed per chunk) and reroutes the WHOLE recording to
            // AssemblyAI rather than returning a transcript full of gap markers — a
            // degraded transcript beats no transcript when the engine is provably down.
            // See docs/dtp-runs/2026-06-01-afrikaans-chunked-resilience.md.
            addLog(`Engine: Gemini 2.5 Flash (Afrikaans) | chunked — ${Math.floor(duration / 60)}min recording…`, 'info');
            const chunkSource = originalFile ?? sourceBlob;
            const chunkedResult = await transcribeAfrikaansChunkedSequential(chunkSource, service, addLog, checkAborted, fallbackToNonGemini);
            const qIssue = isBadTranscript(chunkedResult);
            if (qIssue) {
              geminiCircuitBreaker.recordFailure();
              throw new Error(
                `Afrikaans transcription quality check failed (${qIssue}). This usually clears on a ` +
                  `retry — please try again. (Afrikaans is not routed to AssemblyAI for a quality miss, which mistranscribes it as Dutch.)`,
              );
            }
            geminiCircuitBreaker.recordSuccess();
            result = chunkedResult;
          } else {
            result = await fallbackToNonGemini();
          }

          // Clean up Supabase file (best-effort).
          // POPIA: if this delete fails for any reason, the server-side cron
          // sweeper (/api/profile?action=cron-sweep-audio, every 15 min) will
          // catch the orphan within 1h of upload. We surface errors in the log
          // rather than silently swallowing so storage leaks are visible.
          supabase.storage.from('lp-audio').remove([fileName]).catch((err) => {
            addLog(`Audio cleanup failed (will be swept server-side within 24h): ${err instanceof Error ? err.message : String(err)}`, 'warning');
          });

        } else {
          // ── Small file (<4MB): direct base64 → Gemini 2.5 Flash ──
          const isVideoFile =
            sourceBlob.type.startsWith('video/') ||
            /\.(mp4|webm|mov|avi|mkv)$/i.test((sourceBlob as File).name ?? '');

          if (isVideoFile) {
            // ── Video path: Gemini native video understanding (transcript + scene descriptions) ──
            addLog(`Engine: Gemini 2.5 Flash (video) | Preparing video (${fileSizeMB}MB)...`, 'info');
            const arrayBuffer = await sourceBlob.arrayBuffer();
            const bytes = new Uint8Array(arrayBuffer);
            let binary = '';
            const SLICE = 0x8000;
            for (let i = 0; i < bytes.length; i += SLICE) {
              binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + SLICE, bytes.length)));
            }
            const base64Video = btoa(binary);

            setStep(AppStep.TRANSCRIBING);
            checkAborted();
            setProgress(20);
            addLog('Analysing video — transcript + scene descriptions...', 'info');

            try {
              result = await service.transcribeVideoWithGemini(base64Video, mimeType || 'video/mp4', addLog);
              geminiCircuitBreaker.recordSuccess();
              if (result.sceneDescriptions?.length) {
                addLog(`Video analysis complete: ${result.sceneDescriptions.length} scene description(s) captured.`, 'success');
              }
            } catch (videoErr: unknown) {
              const _vMsg = videoErr instanceof Error ? videoErr.message : String(videoErr);
              addLog(`Video transcription failed (${_vMsg.slice(0, 80)}). Falling back to audio extraction...`, 'warning');
              recordGeminiFailure(videoErr);
              // Fallback: re-encode as audio (browser can decode most video formats)
              const _profile2f = useAuthStore.getState().profile;
              const _lang2f = (_profile2f?.default_language ?? 'auto') as string;
              const _rawEng3f = _profile2f?.default_engine ?? 'auto';
              const _engine2f = (_rawEng3f === 'assemblyai' ? 'auto' : _rawEng3f) as 'auto' | 'deepgram' | 'groq';
              const _effLang2f = sttLanguage ?? (_lang2f === 'auto' ? undefined : _lang2f);
              if (!isEngineAllowed(_effLang2f, 'deepgram')) throw new Error(engineUnavailableMessage(_effLang2f));
              assertEngineAllowed(_effLang2f, 'deepgram');
              assertEngineAllowed(_effLang2f, 'groq');
              result = await service.transcribe(base64Video, mimeType, addLog, { engine: _engine2f, language: _effLang2f ?? _lang2f, diarize: true });
            }
          } else {
            // ── Audio path: base64 → prompted Gemini path (GEMINI_TRANSCRIBE_MODEL) ──
            addLog(`Engine: Gemini (prompted, multilingual) | Preparing audio (${fileSizeMB}MB)...`, 'info');
            const arrayBuffer = await sourceBlob.arrayBuffer();
            const bytes = new Uint8Array(arrayBuffer);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) {
              binary += String.fromCharCode(bytes[i]);
            }
            const base64 = btoa(binary);

            setStep(AppStep.TRANSCRIBING);
            checkAborted();
            setProgress(20);
            addLog('Transcribing audio with speaker diarisation...', 'info');

            const _profile2 = useAuthStore.getState().profile;
            const _lang2 = (_profile2?.default_language ?? 'auto') as string;
            const _diarize2 = _profile2?.default_diarise ?? true;
            const _rawEng3 = _profile2?.default_engine ?? 'auto';
            const _engine2 = (_rawEng3 === 'assemblyai' ? 'auto' : _rawEng3) as 'auto' | 'deepgram' | 'groq';
            // SA-indigenous (beta): ElevenLabs Scribe FIRST, prompted Gemini path as fallback (do-not-translate hint + English-leak retry; see URL path above).
            const _saLang2 = GEMINI_ONLY_LANGS.includes(sttLanguage ?? '') ? sttLanguage : undefined;
            // Fallback routing is governed by engineRouting (af/SA-indigenous: no non-Gemini fallback).
            const _smallFileFallback = _saLang2
              ? () => service.transcribeWithGemini(base64, mimeType, addLog, undefined, _saLang2)
              : () => {
                  // Restricted languages (af, SA-indigenous) never fall back to Deepgram/Groq.
                  const _effLang2 = sttLanguage ?? (_lang2 === 'auto' ? undefined : _lang2);
                  if (!isEngineAllowed(_effLang2, 'deepgram')) throw new Error(engineUnavailableMessage(_effLang2));
                  assertEngineAllowed(_effLang2, 'deepgram');
                  assertEngineAllowed(_effLang2, 'groq');
                  return service.transcribe(base64, mimeType, addLog, { engine: _engine2, language: _effLang2 ?? _lang2, diarize: _diarize2 });
                };
            const _smallFilePrimary = _saLang2
              ? () => service.transcribeWithElevenLabs(base64, mimeType, addLog, _saLang2)
              : () => service.transcribeWithGemini(base64, mimeType, addLog, undefined, sttLanguage);
            result = await transcribeWithFallback(_smallFilePrimary, _smallFileFallback);
          }
        }

        checkAborted();
        // SA-indigenous beta guard: warn if Gemini appears to have translated to
        // English rather than transcribed in-language (never silently pass it off).
        if (GEMINI_ONLY_LANGS.includes(sttLanguage ?? '') && looksLikeEnglishTranslation(result.transcript).flagged) {
          addLog(`⚠ This ${(sttLanguage ?? '').toUpperCase()} transcription (beta) looks like it may have been translated to English rather than transcribed in-language. Please verify the output.`, 'warning');
        }
        // Pass userLanguage so it's saved even when engine doesn't echo back the language (e.g. Gemini)
        const _savedProfile = useAuthStore.getState().profile;
        const _savedLang = (_savedProfile?.default_language ?? 'auto') as string;
        rejectUndetectableDutchAfrikaans(result);
        await handleResult(result, directives, customInstructions, service, checkAborted, title, _savedLang, meetingLanguages, contentHash);

        // ── Visual Speaker Identification (background, non-blocking) ──────────
        // Extract video frames and ask Gemini Vision to detect names on screen.
        // Runs after transcript is displayed — failure never blocks the main result.
        void (async () => {
          try {
            // Determine video blob source
            let videoBlob: Blob | null = null;

            if (inputType === 'local' && sourceBlob) {
              const isVideoFile =
                sourceBlob.type.startsWith('video/') ||
                /\.(mp4|webm|mov|avi|mkv)$/i.test((sourceBlob as File).name ?? '');
              if (isVideoFile) {
                videoBlob = sourceBlob;
              }
            } else if (inputType === 'url' && remoteUrl) {
              // Fetch URL to blob so we can seek frames client-side
              try {
                const urlResp = await fetch(remoteUrl);
                const contentType = urlResp.headers.get('content-type') ?? '';
                if (contentType.startsWith('video/')) {
                  videoBlob = await urlResp.blob();
                }
              } catch {
                // URL fetch failed silently — not a blocking error
              }
            }

            if (!videoBlob) {
              addLog('No video track — no on-screen speaker names to read.', 'info');
              return;
            }

            // Block export until visual ID completes (success or failure)
            setVisualIdPending(true);
            addLog('Scanning video frames for speaker names...', 'info');

            // Build frame timestamps — prioritise frames that are most likely to show
            // name overlays for EACH speaker. Cap at 28 frames (server accepts 30, Vercel
            // 4.5MB body limit is safe at 1280px/q0.72 ≈ 60-130KB/frame → 28 ≈ 1.7-3.6MB).
            //
            // Strategy:
            //   1. First 5 min (every 30s) — intro slides / welcome screen name tiles
            //   2. Last 3 min (every 30s) — closing screens often show all participants
            //   3. First utterance of EACH speaker (lower-third fires when they start)
            //   4. All speaker-change points throughout (transitions trigger name overlays)
            // Merge + dedup + sort + cap at 28.
            const MAX_VISUAL_FRAMES = 28;
            const videoDuration = result.duration; // seconds

            // 1. First 5 minutes — every 30s (11 anchors: 0, 30, 60 ... 300)
            const startAnchorTimestamps: number[] = [];
            for (let t = 0; t <= Math.min(300, videoDuration); t += 30) {
              startAnchorTimestamps.push(t);
            }

            // 2. Last 3 minutes — every 30s (up to 6 anchors).
            //    Only added when recording is longer than the first-5-min window,
            //    and deduped so short recordings don't repeat the same timestamps.
            const endAnchorTimestamps: number[] = [];
            if (videoDuration > 300) {
              const endWindowStart = Math.max(videoDuration - 180, 300);
              for (let t = endWindowStart; t < videoDuration; t += 30) {
                endAnchorTimestamps.push(Math.floor(t));
              }
              // Always include a frame near the very end (closing slide / goodbye screen)
              endAnchorTimestamps.push(Math.floor(Math.max(0, videoDuration - 5)));
            }

            // 3. First appearance of each unique speaker
            const speakerFirstAppearance = new Map<number, number>();
            for (const u of result.utterances) {
              if (!speakerFirstAppearance.has(u.speaker)) {
                speakerFirstAppearance.set(u.speaker, u.start);
              }
            }
            const firstAppearanceTimes = Array.from(speakerFirstAppearance.values());

            // 4. ALL speaker-change points throughout the full recording
            const changeTimestamps: number[] = [];
            for (let i = 1; i < result.utterances.length; i++) {
              if (result.utterances[i].speaker !== result.utterances[i - 1].speaker) {
                changeTimestamps.push(result.utterances[i].start);
              }
            }

            // Merge, deduplicate, sort.
            // End anchors go in before the cap so they survive even on long recordings where
            // speaker-change timestamps could otherwise crowd them out.
            const dedupedMiddle = Array.from(
              new Set([...firstAppearanceTimes, ...changeTimestamps])
            ).sort((a, b) => a - b);

            // Reserve slots: start (11) + end (≤7) + fill remainder with speaker transitions
            const reservedSlots = startAnchorTimestamps.length + endAnchorTimestamps.length;
            const fillSlots = Math.max(0, MAX_VISUAL_FRAMES - reservedSlots);
            const fillTimestamps = dedupedMiddle.slice(0, fillSlots);

            const allTimestamps = Array.from(
              new Set([...startAnchorTimestamps, ...endAnchorTimestamps, ...fillTimestamps])
            )
              .sort((a, b) => a - b)
              .slice(0, MAX_VISUAL_FRAMES);

            // extractFrames checks blob.type.startsWith('video/') internally.
            // For blobs fetched from a URL the MIME may already be set; for local
            // File objects it comes from the OS. Override type if blank to ensure
            // the hook doesn't bail out early.
            let blobForExtraction: Blob = videoBlob;
            if (!videoBlob.type.startsWith('video/')) {
              blobForExtraction = new Blob([videoBlob], { type: 'video/mp4' });
            }

            // Extract frames — returns base64 strings in the same order as allTimestamps.
            // extractFrames skips a frame (no push) only if canvas.toDataURL returns empty,
            // which is extremely rare. We pair frames with timestamps positionally.
            const frames = await extractFrames(blobForExtraction, allTimestamps);

            if (frames.length === 0) {
              addLog('Frame extraction returned no frames — skipping visual ID.', 'warning');
              return;
            }

            // Pair frames with their timestamps positionally (frames[i] ↔ allTimestamps[i]).
            // If fewer frames came back than timestamps (rare failed seeks), trim timestamps.
            const validTimestamps = allTimestamps.slice(0, frames.length);

            // Build per-frame audio transcript context.
            // For each frame timestamp, find the utterance active at that moment so
            // Gemini can use WHAT WAS SAID as the primary signal for who is speaking —
            // far more reliable than visual-only detection in compressed meeting videos.
            const frameTranscripts = validTimestamps.map((ts) => {
              // Exact: utterance covering this timestamp
              const exact = result.utterances.find((u) => u.start <= ts && ts <= u.end);
              if (exact) return { speaker: exact.speaker, text: exact.transcript };
              // Gap-fill: nearest utterance within 3 s (handles silence / brief pauses)
              let nearest: (typeof result.utterances)[0] | null = null;
              let nearestDist = Infinity;
              for (const u of result.utterances) {
                const dist = Math.min(Math.abs(u.start - ts), Math.abs(u.end - ts));
                if (dist < nearestDist && dist <= 3) { nearestDist = dist; nearest = u; }
              }
              return nearest ? { speaker: nearest.speaker, text: nearest.transcript } : null;
            });

            // Resize frames before sending to stay under Vercel's 4.5MB body limit.
            // Text legibility for Gemini OCR requires adequate resolution AND quality.
            //
            // Quality chain: extractFrames captures at q0.92 (constants.ts FRAME_QUALITY).
            // Shrink step re-encodes at q0.72 — effective quality ~0.66, text remains legible
            // while staying well under Vercel's 4.5MB body limit for 20 frames.
            //
            // Size estimate: 1280px × q0.72 ≈ 60–130KB per frame → 20 frames ≈ 1.2–2.6MB ✅
            // (Previously: 640px × q0.3 → ~15–40KB per frame — text crushed and unreadable)
            const MAX_FRAME_WIDTH = 1280;
            const FRAME_SEND_QUALITY = 0.72;
            const shrinkFrame = (b64: string): Promise<string> =>
              new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                  const scale = Math.min(1, MAX_FRAME_WIDTH / img.width);
                  const canvas = document.createElement('canvas');
                  canvas.width = Math.round(img.width * scale);
                  canvas.height = Math.round(img.height * scale);
                  const ctx = canvas.getContext('2d');
                  if (!ctx) { resolve(b64); return; }
                  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                  const shrunk = canvas.toDataURL('image/jpeg', FRAME_SEND_QUALITY).split(',')[1];
                  resolve(shrunk || b64);
                };
                img.onerror = () => resolve(b64);
                img.src = `data:image/jpeg;base64,${b64}`;
              });

            const shrunkFrames = await Promise.all(frames.map(shrinkFrame));

            // Call server-side Gemini vision endpoint — abort after 30 s to prevent
            // the export block from hanging if the Vercel function is slow or stalls.
            const visualIdController = new AbortController();
            const visualIdTimeout = setTimeout(() => visualIdController.abort(), 30_000);
            const idRes = await fetch('/api/intelligence', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              signal: visualIdController.signal,
              body: JSON.stringify({
                action: 'identify-speakers',
                frames: shrunkFrames,
                frameTimestamps: validTimestamps,
                // Audio context for each frame — lets Gemini use speech content as
                // the primary speaker signal rather than guessing from visual cues alone.
                frameTranscripts,
                utterances: result.utterances.map((u) => ({
                  start: u.start,
                  end: u.end,
                  speaker: u.speaker,
                  transcript: u.transcript,
                })),
                speakerCount: result.speakers,
              }),
            });

            clearTimeout(visualIdTimeout);
            if (!idRes.ok) {
              const errData = await idRes.json().catch(() => ({})) as { error?: string };
              const errMsg = errData.error || idRes.statusText || 'unknown error';
              addLog(`Visual ID skipped (HTTP ${idRes.status}): ${errMsg}`, 'warning');
              return;
            }

            const idData = await idRes.json() as {
              speakers: Record<string, string>;
              detectedCount: number;
            };

            if (idData.detectedCount === 0 || Object.keys(idData.speakers).length === 0) {
              addLog('Visual ID: No speaker names found in video frames.', 'info');
              return;
            }

            // Convert string keys: AssemblyAI uses 0-based speaker indices,
            // but the transcript labels are 1-based ("Speaker 1", "Speaker 2", ...).
            // Add 1 so suggestedNames[1] matches "Speaker 1" in SpeakerNamer.
            const numericSpeakers: Record<number, string> = {};
            for (const [k, v] of Object.entries(idData.speakers)) {
              numericSpeakers[parseInt(k, 10) + 1] = v;
            }

            setSuggestedSpeakerNames(numericSpeakers);
            const nameList = Object.values(numericSpeakers).join(', ');
            addLog(`Visual ID detected: ${nameList} — applying to transcript automatically`, 'success');

            // Auto-apply: replace "Speaker N" labels in the transcript and speaker map.
            // Sort descending so "Speaker 10" is replaced before "Speaker 1".
            //
            // Safety: if the same name would be applied to MULTIPLE speaker slots,
            // skip all of them — a name that maps to every speaker is not reliable
            // (e.g., persistent watermark). The server-side dedup should have already
            // filtered these, but this is a client-side safety net.
            const autoApplyNameCounts = new Map<string, number>();
            for (const v of Object.values(numericSpeakers)) {
              autoApplyNameCounts.set(v, (autoApplyNameCounts.get(v) ?? 0) + 1);
            }

            const sortedNums = Object.keys(numericSpeakers)
              .map(Number)
              .sort((a, b) => b - a);

            let updatedTranscript = useTranscriptionStore.getState().finalTranscript;
            let updatedSpeakerMap = useTranscriptionStore.getState().speakerMap;

            const skippedNames: string[] = [];
            for (const n of sortedNums) {
              const name = numericSpeakers[n]?.trim();
              if (!name) continue;
              // Skip if the same name appears for more than one speaker index
              if ((autoApplyNameCounts.get(name) ?? 0) > 1) {
                skippedNames.push(name);
                continue;
              }
              updatedTranscript = updatedTranscript.split(`**Speaker ${n}**`).join(`**${name}**`);
              updatedTranscript = updatedTranscript.split(`Speaker ${n}`).join(name);
              updatedSpeakerMap = updatedSpeakerMap.split(`**Speaker ${n}**`).join(`**${name}**`);
              updatedSpeakerMap = updatedSpeakerMap.split(`Speaker ${n}`).join(name);
            }
            const uniqueSkipped = [...new Set(skippedNames)];
            if (uniqueSkipped.length > 0) {
              addLog(`Visual ID: skipped ambiguous name${uniqueSkipped.length > 1 ? 's' : ''} (same name mapped to multiple speakers — enter manually): ${uniqueSkipped.join(', ')}`, 'warning');
            }

            setFinalTranscript(updatedTranscript);
            setSpeakerMap(updatedSpeakerMap);
          } catch (visualErr) {
            // Non-blocking — log warning but don't surface to user as an error
            if (visualErr instanceof Error && visualErr.name === 'AbortError') {
              addLog('Visual ID timed out (30 s) — export available now.', 'warning');
            } else {
              console.warn('Visual speaker ID failed:', visualErr);
              addLog('Visual speaker ID unavailable — enter names manually if needed.', 'warning');
            }
          } finally {
            // Always unblock export — visual ID is done (success, failure, or skip)
            setVisualIdPending(false);
          }
        })();

      } catch (err: unknown) {
        if (err instanceof Error && err.message === 'PIPELINE_CANCELLED') {
          addLog('Pipeline cancelled by user.', 'warning');
          return;
        }
        const errorMessage = err instanceof Error ? err.message : 'Transcription failed.';
        console.error(err);
        setError(errorMessage);
        setSystemStatus(SYSTEM_STATUS.ERROR);
        setStep(AppStep.ERROR);
        await ForensicAudio.playError();
      } finally {
        cleanup();
      }
    },
    [decodeAudio, extractFrames, cleanup, getService, handleResult, setStep, setProgress, setSystemStatus, setError, addLog, setSuggestedSpeakerNames, setFinalTranscript, setSpeakerMap, setVisualIdPending]
  );

  /**
   * Cancel the current pipeline execution
   */
  const cancelPipeline = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    cleanup();
    setStep(AppStep.IDLE);
    setProgress(0);
    setSystemStatus(SYSTEM_STATUS.NOMINAL);
    setError(null);
    addLog('Pipeline cancelled by user.', 'warning');
  }, [cleanup, setStep, setProgress, setSystemStatus, setError, addLog]);

  return { executePipeline, cancelPipeline };
}
